import React, { useMemo, useState } from "react";
import useSWR from "swr";
import type { ColumnDef } from "@tanstack/react-table";
import styled from "styled-components";
import { H3 } from "../../../../components/Typography/Typography";
import { Loader } from "../../../../components/Loader/Loader";
import { Pagination } from "../../../../components/Pagination/Pagination";
import { ProductCardsGrid } from "../../../../components/ProductCardsGrid/ProductCardsGrid";
import { Table } from "../../../../components/Table/Table";
import {
  getProductStatusColor,
  getProductStatusText,
  StatusLeft,
} from "../../../../components/Status/Status";
import type { ViewMode } from "../../../../components/TablePage/TableViewToggle";
import { TableViewToggle } from "../../../../components/TablePage/TableViewToggle";
import { endpoints } from "../../../../endpoints";
import {
  DEFAULT_PER_PAGE,
  TablePlaceholder,
  useStoreState,
} from "../../../../util/util";
import { useRoutePath } from "../../../../util/Routing";
import type { MinimalProductSummary } from "../../OrganizationPage/ProductsList/ProductsList";
import { useTranslation } from "react-i18next";
import type {
  PIMProduct,
  ProductReference,
  ProductStatusType,
} from "../../../../types/types.PIM";
import type { WithPagination } from "../../../../types/types";
import type { ProductCardGridItem } from "../../../../components/ProductCardsGrid/ProductCardsGrid";
import { PROFILE_DETAIL_CLASSES } from "../../../../layout/shared/ProfileDetailLayout";
import { useAuthContext } from "../../../../components/Auth";
import { ScrollTableWrapper } from "../Assets/DocumentsOrDigitalMediaTable/DocumentsOrDigitalMediaTable";
import { IDMobile } from "../../../../components/IDs/IDs";

const VariantsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const VARIANTS_SCROLL_MAX_HEIGHT = "380px";

type VariantTableRow = MinimalProductSummary & { rowURL?: string };

export const ProductVariants: React.FC<{ product: PIMProduct }> = ({
  product,
}) => {
  const { tenant_id } = useStoreState();
  const { hasPermission } = useAuthContext();
  const { adminPath } = useRoutePath();
  const { t } = useTranslation();
  const hasViewPermission = hasPermission(`view_product_type_variant`);
  const isMaster = product.pim_product_type === "master";

  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [offset, setOffset] = useState(0);
  const perPage = DEFAULT_PER_PAGE;

  const swrParams = useMemo(
    () => ({ params: { offset, limit: perPage } }),
    [offset, perPage]
  );

  const { data, error } = useSWR<
    WithPagination<{
      data: MinimalProductSummary[];
    }>
  >(
    isMaster
      ? [
          endpoints.v2_tenants_id_pim_products_id_variants(
            tenant_id,
            product.product_number ??
              (product.status === "staged"
                ? product.published_product_id
                : product.id)
          ),
          swrParams,
        ]
      : null
  );
  const isLoading = !data && !error;

  const pagination = useMemo(
    () => ({
      perPage,
      pageCount: Math.ceil((data?.pagination.total ?? 0) / perPage),
      pageIndex: offset / perPage + 1,
    }),
    [data?.pagination.total, offset, perPage]
  );

  const items = useMemo<ProductCardGridItem[]>(() => {
    if (!data?.data || !isMaster) return [];
    return data.data.map((variant) => {
      const status: ProductStatusType =
        (variant.display_status as ProductStatusType) ??
        (variant.status as ProductStatusType) ??
        "draft";
      const productReference: ProductReference = {
        id: variant.id,
        name: variant.name ?? "",
        slug: variant.product_number,
        product_number: variant.product_number,
        image_url: "",
        product_description: "",
        cas_number: "",
        brand_name: "",
        chemical_name: variant.chemical_name ?? "",
        is_accessible: true,
        is_visible_on_storefronts: true,
        status,
        visibility_tenants: [],
      };
      return {
        id: variant.id,
        product: productReference,
        href: hasViewPermission
          ? `${adminPath}/pim/products/${variant.product_number}`
          : null,
        dynamicPreviewBlocks: [
          { header: t("Product ID"), content: variant.product_number },
          { header: t("Status"), content: getProductStatusText(status, t) },
        ],
      };
    });
  }, [adminPath, data?.data, isMaster, hasViewPermission, t]);

  const tableData = useMemo<VariantTableRow[]>(() => {
    if (!data?.data || !isMaster) return [];
    return data.data.map((variant) => ({
      ...variant,
      rowURL: hasViewPermission
        ? `${adminPath}/pim/products/${variant.product_number}`
        : undefined,
    }));
  }, [adminPath, data?.data, isMaster, hasViewPermission]);

  const tableColumns = useMemo<ColumnDef<VariantTableRow>[]>(
    () => [
      {
        header: t("Product ID"),
        accessorKey: "product_number",
        id: "product_number",
        cell: ({ getValue }) => <IDMobile>{getValue() as string}</IDMobile>,
      },
      {
        header: t("Name"),
        accessorKey: "name",
        id: "name",
        cell: ({ getValue }) => (getValue() as string) || "--",
      },
      {
        header: t("Status"),
        accessorKey: "status",
        id: "status",
        cell: ({ row }) => {
          const status: ProductStatusType =
            (row.original.display_status as ProductStatusType) ??
            (row.original.status as ProductStatusType) ??
            "draft";
          return (
            <StatusLeft
              color={getProductStatusColor(status)}
              text={getProductStatusText(status, t)}
              textStyle={{ fontSize: "13px" }}
              productStatusType="product"
            />
          );
        },
      },
    ],
    [t]
  );

  const changePage = (newOffset: number) => setOffset(newOffset);

  return isMaster ? (
    <div className={PROFILE_DETAIL_CLASSES.collectionCard}>
      <VariantsHeader>
        <H3>{t("Product Variants")}</H3>
        <TableViewToggle value={viewMode} onChange={setViewMode} />
      </VariantsHeader>
      <div style={{ position: "relative", minHeight: isLoading ? 200 : 0 }}>
        <Loader isLoading={isLoading} />
        {!isLoading && viewMode === "table" && (
          <ScrollTableWrapper maxHeight={VARIANTS_SCROLL_MAX_HEIGHT}>
            <Table
              columns={tableColumns}
              data={tableData}
              isLoading={isLoading}
              error={error}
              Placeholder={
                <TablePlaceholder message={t("No variants to show.")} />
              }
            />
          </ScrollTableWrapper>
        )}
        {!isLoading && viewMode === "grid" && (
          <ScrollTableWrapper maxHeight={VARIANTS_SCROLL_MAX_HEIGHT}>
            <ProductCardsGrid
              items={items}
              emptyMessage={t("No variants to show.")}
            />
          </ScrollTableWrapper>
        )}
      </div>
      <Pagination
        pagination={pagination}
        offset={offset}
        handlePageClick={changePage}
      />
    </div>
  ) : null;
};
